// Background Layer Component
// Component to display background image with fallback color and smooth transitions.
// Styles without background art (useStyleHasArt: Daylight, Luxe) get a plain layer painted with
// the style's --k-page-background instead: no admin image, fallback colour or dimming overlay.

import { useEffect, useState, useRef } from 'react';
import { useStyleHasArt } from '@/hooks/useStyleHasArt';
import type { BackgroundConfig } from '../types/vfxConfig';

interface BackgroundLayerProps {
  config: BackgroundConfig | null;
}

export default function BackgroundLayer({ config }: BackgroundLayerProps) {
  const hasArt = useStyleHasArt();
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const previousUrlRef = useRef<string | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    // No art in this style: nothing to preload (the image would never be shown).
    if (!hasArt || !config || !config.enabled) {
      setCurrentUrl(null);
      setImageError(false);
      return;
    }

    // If URL is null, use fallback color only
    if (!config.url || config.url === null) {
      setCurrentUrl(null);
      setImageError(false);
      setIsLoading(false);
      previousUrlRef.current = null;
      return;
    }

    // If URL hasn't changed, don't reload
    if (config.url === previousUrlRef.current && currentUrl === config.url) {
      return;
    }

    // Preload image before displaying
    setIsLoading(true);
    setImageError(false);
    previousUrlRef.current = config.url;

    const img = new Image();

    img.onload = () => {
      setIsLoading(false);
      setCurrentUrl(config.url);
      setImageError(false);
    };
    img.onerror = () => {
      setIsLoading(false);
      setImageError(true);
      setCurrentUrl(null);
    };
    img.src = config.url;
    imageRef.current = img;
  }, [config, currentUrl, hasArt]);

  if (!hasArt) {
    return (
      <div
        data-testid="page-background"
        className="pointer-events-none absolute inset-0 -z-1 bg-page"
        aria-hidden="true"
      />
    );
  }

  // Get background color (fallback or transparent if disabled)
  const backgroundColor = config?.enabled && config?.fallback
    ? config.fallback
    : 'transparent';

  // Get background image URL (only if loaded successfully)
  const backgroundImage = currentUrl && !imageError && !isLoading
    ? `url(${currentUrl})`
    : 'none';

  return (
    <>
      {/* Background Image Layer. Transitions are classes, not inline styles, so the
          "Animations off" rule in globals.css can switch them off. */}
      <div
        data-testid="page-background-art"
        className="transition-[background-color,background-image] duration-500 ease-[ease]"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          minWidth: '100%',
          minHeight: '100%',
          zIndex: -1,
          backgroundColor,
          backgroundImage,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      {/* Overlay to blend with UI */}
      {config?.enabled && backgroundImage !== 'none' && (
        <div
          className="transition-[background-color] duration-500 ease-[ease]"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
}
